import { Box, Divider, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import {
  ActivityHeader_LeaderFragment,
  ClassDetailsNoSectionsQuery,
} from "@outschool/gql-frontend-generated";
import {
  faBirthdayCake,
  faCalendarAlt,
  faCheck,
  faClock,
  faSignalStream,
  faVideoSlash,
} from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import { gql } from "@outschool/ui-apollo";
import { Container } from "@outschool/ui-legacy-component-library";
import { useDevicePixelRatio } from "@outschool/ui-utils";
import React, { useState } from "react";

import usePriceDetailsString from "../hooks/usePriceDetailsString";
import { SelfPacedIconBullets, getClassTypeIcon } from "../icons";
import { IconBullet } from "../PriceDetailsComponents";
import { SaveButtonActivityFragment } from "../Saving/graphql/fragments";
import TeacherNameAndRatings from "../Teachers/TeacherNameAndRatings";
import useActivityString from "../translations/useActivityString";
import { ActivitySize } from "./ActivityDetails";
import ActivityOverview from "./ActivityOverview";
import ActivityPhoto from "./ActivityPhoto";
import { TeacherVideoModal } from "./TeacherVideoModal";
import { WatchMyTeacherVideoLink } from "./WatchMyTeacherVideoLink";
import { EsaEligibleChips } from "./EsaEligibleChips";

const MAIN_IMAGE_HEIGHT = 179;
const MAIN_IMAGE_WIDTH = 344;

export default function MobileActivityHeader({
  activity,
  leader,
  isActivitySavedByCurrentUser,
  facebookAppId,
  onClickViewReviews = () => {},
  MessageTeacherButton,
  BecomeAMemberBanner,
}: {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ActivityHeader_LeaderFragment;
  isActivitySavedByCurrentUser?: boolean;
  facebookAppId?: string;
  onClickViewReviews?: (event: React.SyntheticEvent) => void;
  MessageTeacherButton?: React.JSX.Element;
  BecomeAMemberBanner?: React.ReactNode;
}) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\MobileActivityHeader"
  );

  const imageUrl = React.useMemo(() => {
    return FilestackUrls.imageUrl(activity.details.photo, {
      w: MAIN_IMAGE_WIDTH,
      h: MAIN_IMAGE_HEIGHT,
      fit: "crop",
    });
  }, [activity.details.photo]);
  const pixelRatio = useDevicePixelRatio();
  const imageWidth = MAIN_IMAGE_WIDTH * pixelRatio;
  const imageHeight = MAIN_IMAGE_HEIGHT * pixelRatio;
  const showTeacherVideoLink = !activity?.video && !!activity?.leader?.video;
  const [teacherVideoModalOpen, setTeacherVideoModalOpen] = useState(false);

  const {
    classDurationShortString,
    classFormatString,
    suggestedWeeklyMeetingsString,
    weeklyMeetingsSearchString,
    videoMeetingsString,
  } = usePriceDetailsString();
  const { ageRangeString } = useActivityString();
  const showSuggestedWeeklyMeetings =
    activity.suggestedWeeklyMeetings && activity.suggestedWeeklyMeetings > 1;

  return (
    <>
      <TeacherVideoModal
        isOpen={teacherVideoModalOpen}
        onClose={() => setTeacherVideoModalOpen(false)}
        teacher={leader}
        MessageTeacherButton={MessageTeacherButton}
      />
      <ActivityPhoto
        activity={activity}
        imageUrl={imageUrl}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        containerHeight={`${(100 * MAIN_IMAGE_HEIGHT) / MAIN_IMAGE_WIDTH}vw`}
        isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
        facebookAppId={facebookAppId}
        isMobileCdp={true}
      />
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5em",
        }}
      >
        <Box flex sx={{ flexDirection: "column", gap: "0.75em" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              marginTop: "1em",
            }}
          >
            <Box sx={{ width: "fit-content" }}>{BecomeAMemberBanner}</Box>
            <EsaEligibleChips activity={activity} />
          </Box>
          <Typography variant="h3" component="h1">
            {activity.titleTranslated}
          </Typography>
          <ActivityRatings
            activity={activity}
            leader={leader}
            t={t}
            onClickViewReviews={onClickViewReviews}
          />
          <Box flex sx={{ columnGap: "1em" }}>
            <IconBullet
              icon={faBirthdayCake}
              text={t("Ages {{ageRange}}", {
                ageRange: ageRangeString(activity, false),
              })}
              typographyVariant="subtitle1"
            />
            <IconBullet
              icon={getClassTypeIcon(activity)}
              text={classFormatString(activity)}
              typographyVariant="subtitle1"
            />
          </Box>
        </Box>
        <>
          <Typography variant="body1">{activity.summaryTranslated}</Typography>
        </>
        <Box flex sx={{ flexDirection: "column", gap: "0.75em" }}>
          <TeacherNameAndRatings
            leader={leader}
            mobileCdp={true}
            onClickViewReviews={onClickViewReviews}
          />
          {showTeacherVideoLink && (
            <WatchMyTeacherVideoLink
              activity={activity}
              openModal={() => setTeacherVideoModalOpen(true)}
            />
          )}
        </Box>
        <Divider sx={{ borderColor: "gray100" }} />
        <Box flex sx={{ gap: "0.5em", flexDirection: "column" }}>
          {activity.isSelfPaced ? (
            <SelfPacedIconBullets activity={activity} />
          ) : (
            <>
              <IconBullet
                icon={
                  !activity.isFlexSchedule && !activity.isClub
                    ? faSignalStream
                    : faVideoSlash
                }
                text={videoMeetingsString(activity)}
              />
              {showSuggestedWeeklyMeetings && (
                <IconBullet
                  icon={faCalendarAlt}
                  text={suggestedWeeklyMeetingsString(activity)}
                />
              )}
              <IconBullet
                icon={faCalendarAlt}
                text={weeklyMeetingsSearchString(activity)}
              />

              {!activity.isClub && activity.size_max !== 1 && (
                <ActivitySize activity={activity} useIconBullet={true} />
              )}
              {!activity.isClub && !activity.isFlexSchedule && (
                <IconBullet
                  icon={faClock}
                  text={classDurationShortString(activity)}
                />
              )}
            </>
          )}
        </Box>
        <Divider sx={{ borderColor: "gray100" }} />
      </Container>
    </>
  );
}

function ActivityRatings({
  activity,
  leader,
  t,
}: {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ActivityHeader_LeaderFragment;
  t: TFunction;
  onClickViewReviews?: (event: React.SyntheticEvent) => void;
}) {
  const leaderHasReviews = leader.reviewCount > 0;
  const activityHasReviews = activity.reviewCount > 0;
  const activityCompletedEnrollmentCount =
    activity.completedEnrollmentCount || 0;
  const activityHasCompletedEnrollments = activityCompletedEnrollmentCount > 0;
  const text = t("Completed by {{count}} learner", {
    count: activity.completedEnrollmentCount,
    defaultValue_plural: "Completed by {{count}} learners",
  });

  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        gap: "16px",
      }}
    >
      {activity.isClub === false && activityHasCompletedEnrollments && (
        <IconBullet icon={faCheck} iconColor="info" text={text} />
      )}
      {leaderHasReviews &&
        !activityHasReviews &&
        !activityHasCompletedEnrollments &&
        !activity.isClub && (
          <Typography
            variant="body1"
            color="grey.600"
          >{t`New class`}</Typography>
        )}
    </Box>
  );
}

const SubjectLinkFragments = {
  activity: gql`
    fragment SubjectLink_activity on Activity {
      uid
      subject
    }
  `,
};

MobileActivityHeader.fragments = {
  activity: gql`
    fragment ActivityHeader_activity on Activity {
      uid
      isClub
      summary
      summaryTranslated
      title
      titleTranslated
      slug_id
      isSoldOut
      video {
        file {
          url
          thumbUrl
        }
      }
      sections {
        uid
        filledSpaceCount
      }
      ...ActivityOverview_activity
      ...SubjectLink_activity
      ...SaveButton_activity
    }
    ${ActivityOverview.fragments.activity}
    ${SubjectLinkFragments.activity}
    ${SaveButtonActivityFragment}
  `,
  leader: gql`
    fragment ActivityHeader_leader on Leader {
      uid
      publishedClassCount
      name
      suspended_at
      ...ActivityOverview_leader
    }
    ${ActivityOverview.fragments.leader}
  `,
};
